'use client'

import styles from 'components/flow-header/FlowHeader.module.scss'
import { Button, Modal, StepIndicator } from '@boltenergy-be/design-system'
import Logo from 'assets/Logo'
import Link from 'components/link/Link'
import { FlowHeaderProps } from 'components/flow-header/types'
import { useTranslation } from 'next-i18next/pages'
import { RegistrationSteps } from 'features/registration/types'
import { handlePrevious } from 'features/simulation/layouts/utils'
import { SimulationSteps } from 'features/simulation/types'
import { useRouter } from 'next/router'
import { useGetLanguage } from 'hooks/useGetLanguage'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { routes } from 'utils/routes'
import { updateMarketingData } from 'store/marketing/slice'
import { MarketingDataKeys } from 'store/marketing/types'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { CommonTrackingParams } from 'types/tracking'
import { CustomerFlows, FlowAlternatives, InputGroupKeys } from 'store/customer/enums'
import BoltPlenty from 'assets/BoltPlenty'
import { trackEvent } from 'utils/tracking'
import { useState } from 'react'
import { resetItsmePrefilled } from 'store/customer/slice'
import { IDENTIFICATION_METHODS } from '@boltenergy-be/shared'
import { INTERNAL_REGISTRATION_URL_PARAMS } from 'constants/customer-flow'
import { checkEnforcePlenty } from 'features/simulation/utils'

const FlowHeader = ({ flow, withoutBackButton }: FlowHeaderProps) => {
  // Redux
  const { currentStep, steps } = useStoreSelector((store) => store.customer.flows[flow])
  const flowAlternative = useStoreSelector((store) => store.customer.flowAlternative)
  const startedFunnelFromPath = useStoreSelector((store) => store.customer.startedFunnelFromPath)
  const { region } = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const { noEanFlow, creditCheckConfirmationFlow, emailConfirmationFlow, fetchSimulationFlow, availableProducts } = useStoreSelector(
    (store) => store.marketing
  )
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['common', 'simulationCard'])
  const language = useGetLanguage()

  // Router
  const router = useRouter()
  const { query } = router
  const isClosedWon = query[INTERNAL_REGISTRATION_URL_PARAMS.IS_CLOSED_WON] === 'true'
  const isInvalidFlow =
    query[INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_NO_EAN_FLOW] === 'true' ||
    query[INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_CREDIT_CHECK_FLOW] === 'true'

  // Constants
  const isFromPlentyFlow = flowAlternative[FlowAlternatives.PLENTY]
  const enforcePlenty = checkEnforcePlenty({ region, availableProducts })
  const isConfirmation = currentStep === RegistrationSteps.CONFIRMATION
  const isRegistrationOnlyFlow =
    currentStep === RegistrationSteps.DELIVERY_ADDRESS &&
    (noEanFlow || emailConfirmationFlow || creditCheckConfirmationFlow || fetchSimulationFlow)
  const hideBackButton = withoutBackButton || isConfirmation || isRegistrationOnlyFlow || isClosedWon || isInvalidFlow
  const activeStepIndex = steps.findIndex((step: RegistrationSteps | SimulationSteps) => step === currentStep)
  const stepsForIndicator = flow === CustomerFlows.REGISTRATION ? steps.filter((step) => step !== RegistrationSteps.CONFIRMATION) : steps

  // Local state
  const [showPreviousConfirmationModal, setShowPreviousConfirmationModal] = useState<boolean>(false)

  return (
    <>
      <header className={styles.header}>
        {!hideBackButton && (
          <Button
            className={styles.back}
            leadingIcon="arrowLeft"
            variant="secondary"
            type="button"
            onClick={() => {
              if (currentStep === RegistrationSteps.DELIVERY_ADDRESS) {
                router.back()
              } else if (currentStep === SimulationSteps.POSTAL_CODE) {
                router.push(startedFunnelFromPath || routes(language).home)
              } else if (currentStep === RegistrationSteps.PERSONAL_DATA && flowAlternative[FlowAlternatives.ITSME]) {
                setShowPreviousConfirmationModal(true)
              } else {
                handlePrevious(flow, currentStep === SimulationSteps.PROPOSITION ? SimulationSteps.USAGE : undefined)
              }
            }}
          >
            {t('previous')}
          </Button>
        )}

        <div className={styles['indicator-group']}>
          {isFromPlentyFlow || (enforcePlenty && currentStep === RegistrationSteps.CONFIRMATION) ? (
            <BoltPlenty className={styles.logo} />
          ) : (
            <Logo className={styles.logo} />
          )}
          {currentStep !== RegistrationSteps.CONFIRMATION && !isInvalidFlow && (
            <>
              <small>
                {t('stepCounter', {
                  current: activeStepIndex + 1,
                  total: stepsForIndicator.length
                })}
              </small>
              <StepIndicator activeIndex={activeStepIndex} totalSteps={stepsForIndicator.length} classnames={{ list: styles.indicator }} />
            </>
          )}
        </div>

        <Link
          className={styles.close}
          representation="button"
          leadingIcon="cross"
          variant="tertiary"
          href={startedFunnelFromPath || routes(language).home}
          suppressHydrationWarning
          onNavigate={() => {
            trackEvent(
              { category: flow === 'registration' ? 'subscription' : flow, object: 'close', action: 'click' },
              { [CommonTrackingParams.STEP]: currentStep }
            )
          }}
          title={t('simulationCard:backToWebsite')}
        />
      </header>

      {/* Confirmation modal if going before itsme authentication */}
      <Modal
        isOpen={showPreviousConfirmationModal}
        onRequestClose={() => setShowPreviousConfirmationModal(false)}
        title={t('itsmeModal.title')}
      >
        <p>{t('itsmeModal.description')}</p>
        <div className="flex-row gap-300 mt-500">
          <Button variant="primary" type="button" onClick={() => setShowPreviousConfirmationModal(false)}>
            {t('itsmeModal.stay')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              handlePrevious(flow, currentStep === SimulationSteps.PROPOSITION ? SimulationSteps.USAGE : undefined)
              // Reset itsme state because re-identification will possibly be necessary
              dispatch(resetItsmePrefilled())
              dispatch(updateMarketingData({ key: MarketingDataKeys.IDENTIFICATION_METHOD, value: IDENTIFICATION_METHODS.default }))
              setShowPreviousConfirmationModal(false)
            }}
          >
            {t('itsmeModal.previous')}
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default FlowHeader
